import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Globe, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const languages = [
  { code: 'ar', name: 'Arabic' },
  { code: 'zh', name: 'Chinese' },
  { code: 'nl', name: 'Dutch' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'hi', name: 'Hindi' },
  { code: 'id', name: 'Indonesian' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ms', name: 'Malay' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'es', name: 'Spanish' },
  { code: 'sv', name: 'Swedish' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'vi', name: 'Vietnamese' },
];

const countryToLanguage = {
  'US': 'en', 'GB': 'en', 'CA': 'en', 'AU': 'en', 'NZ': 'en', 'IE': 'en',
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'CL': 'es', 'PE': 'es',
  'FR': 'fr', 'BE': 'fr', 'CH': 'fr', 'LU': 'fr',
  'DE': 'de', 'AT': 'de',
  'IT': 'it',
  'PT': 'pt', 'BR': 'pt',
  'RU': 'ru', 'BY': 'ru', 'KZ': 'ru',
  'CN': 'zh', 'TW': 'zh', 'HK': 'zh',
  'JP': 'ja',
  'KR': 'ko',
  'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'IQ': 'ar', 'JO': 'ar', 'KW': 'ar', 'LB': 'ar',
  'TR': 'tr', 'CY': 'tr',
  'IN': 'hi',
  'NL': 'nl',
  'PL': 'pl',
  'SE': 'sv',
  'TH': 'th',
  'VN': 'vi',
  'ID': 'id',
  'MY': 'ms',
};

const TRANSLATION_VERSION = 'v11'; // Bump to invalidate stale cache
const RTL_LANGUAGES = ['ar'];

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    detectAndSetLanguage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const detectAndSetLanguage = async () => {
    const savedLanguage = localStorage.getItem('user_language_preference');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
      await changeLanguageWithTranslation(savedLanguage);
      return;
    }

    const autoDetectedSession = sessionStorage.getItem('auto_detected_language');
    if (autoDetectedSession) {
      setCurrentLanguage(autoDetectedSession);
      await changeLanguageWithTranslation(autoDetectedSession);
      return;
    }

    try {
      const response = await axios.get('https://ipapi.co/json/', { timeout: 5000 });
      const countryCode = response.data.country_code;
      const detectedLanguage = countryToLanguage[countryCode] || 'en';
      
      sessionStorage.setItem('auto_detected_language', detectedLanguage);
      setCurrentLanguage(detectedLanguage);
      await changeLanguageWithTranslation(detectedLanguage);
    } catch (error) {
      const browserLang = navigator.language.split('-')[0];
      const langCode = languages.find(l => l.code === browserLang)?.code || 'en';
      setCurrentLanguage(langCode);
      await changeLanguageWithTranslation(langCode);
    }
  };

  const changeLanguageWithTranslation = async (langCode) => {
    // Set RTL direction for Arabic
    document.documentElement.dir = RTL_LANGUAGES.includes(langCode) ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;

    if (langCode === 'en') {
      await i18n.changeLanguage(langCode);
      return;
    }

    // Check if i18n already has a resource bundle (e.g. pre-loaded Arabic)
    if (i18n.hasResourceBundle(langCode, 'translation')) {
      const bundle = i18n.getResourceBundle(langCode, 'translation');
      if (bundle && Object.keys(bundle).length > 50) {
        await i18n.changeLanguage(langCode);
        // Still try to fetch full translations in background to fill any gaps
        fetchFullTranslation(langCode);
        return;
      }
    }

    // Check localStorage cache with version key.
    // NOTE: contains public UI-string translations only — NOT sensitive data.
    // We deliberately use localStorage to survive across sessions so users
    // don't wait for LLM translations on every visit.
    const cacheKey = `translations_${TRANSLATION_VERSION}_${langCode}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const enKeys = Object.keys(i18n.store.data.en.translation);
        if (Object.keys(parsed).length >= enKeys.length * 0.8) {
          i18n.addResourceBundle(langCode, 'translation', parsed, true, true);
          await i18n.changeLanguage(langCode);
          return;
        }
        localStorage.removeItem(cacheKey);
      } catch (e) {
        localStorage.removeItem(cacheKey);
      }
    }

    // Clean up old versioned caches
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith('translations_') && !key.startsWith(`translations_${TRANSLATION_VERSION}_`)) {
        localStorage.removeItem(key);
      }
    }

    // Fetch full translations
    await fetchFullTranslation(langCode);
  };

  const fetchFullTranslation = async (langCode) => {
    setTranslating(true);
    try {
      const enTexts = i18n.store.data.en.translation;
      const langName = languages.find(l => l.code === langCode)?.name || langCode;
      const allKeys = Object.keys(enTexts);
      const chunkSize = 120;

      // Split into chunks and fire in parallel — bottleneck is LLM latency,
      // not bandwidth, so concurrent requests give a 5-10x first-load speedup.
      const chunks = [];
      for (let ci = 0; ci < allKeys.length; ci += chunkSize) {
        const chunkKeys = allKeys.slice(ci, ci + chunkSize);
        const chunk = {};
        chunkKeys.forEach(k => { chunk[k] = enTexts[k]; });
        chunks.push(chunk);
      }

      const results = await Promise.all(chunks.map(chunk =>
        axios.post(`${API}/translate/batch`, { texts: chunk, target_language: langName })
          .then(r => r.data.translations)
          .catch(() => ({})) // tolerate single-chunk failure
      ));

      const allTranslated = Object.assign({}, ...results);

      const cacheKey = `translations_${TRANSLATION_VERSION}_${langCode}`;
      localStorage.setItem(cacheKey, JSON.stringify(allTranslated));

      i18n.addResourceBundle(langCode, 'translation', allTranslated, true, true);
      await i18n.changeLanguage(langCode);
    } catch (error) {
      console.error('Batch translation error:', error);
      if (!i18n.hasResourceBundle(langCode, 'translation')) {
        await i18n.changeLanguage('en');
        setCurrentLanguage('en');
        document.documentElement.dir = 'ltr';
      }
    } finally {
      setTranslating(false);
    }
  };

  const handleLanguageChange = async (langCode) => {
    setCurrentLanguage(langCode);
    localStorage.setItem('user_language_preference', langCode);
    await changeLanguageWithTranslation(langCode);
  };

  return (
    <div className="flex items-center space-x-2" data-testid="language-selector">
      {translating ? (
        <Loader2 className="w-5 h-5 text-[#D96C4A] animate-spin" />
      ) : (
        <Globe className="w-5 h-5 text-[#5C6B61]" />
      )}
      <Select value={currentLanguage} onValueChange={handleLanguageChange}>
        <SelectTrigger className="w-[140px] border-0 bg-transparent focus:ring-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-[300px] bg-white">
          {languages.map((lang) => (
            <SelectItem key={lang.code} value={lang.code}>
              {lang.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default LanguageSelector;
